import React, { useCallback, useContext, useState } from "react";
import { ListContext } from "context";
import { FieldProps } from "./Datagrid/Field/types";
import Datagrid from "./Datagrid/Datagrid";
import { hasKey, isRecord } from "helpers/typing";

interface Props<TRecord> {
    recordIdField?: string;
    hasBulkActions?: boolean;
    onRowClick?: (record: TRecord, e?: React.MouseEvent) => void;
    children: Array<React.ReactElement<FieldProps> | null>;
    withScrollTable?: boolean;
}

const getRecordIdValue = (record: unknown, recordIdField: string): unknown => {
    if (isRecord(record) && hasKey(record, recordIdField)) {
        return record[recordIdField];
    }
    return undefined;
};

const DatagridWithIds = <TRecord,>({
    recordIdField = "id",
    hasBulkActions = false,
    onRowClick,
    withScrollTable = true,
    children,
}: Props<TRecord>) => {
    const { data, ...listContext } = useContext(ListContext);

    const [currentSort, setSort] = useState<{ field?: string; order: "asc" | "desc" }>({
        field: undefined,
        order: "asc",
    });

    const handleOnSelect = useCallback(
        (selectedData: Array<TRecord>) => {
            listContext.setSelectedIds(selectedData.map((record) => getRecordIdValue(record, recordIdField)));
        },
        [listContext, recordIdField]
    );

    return (
        <div className={withScrollTable ? "table-overflow-wrapper" : ""}>
            <Datagrid
                data={data as TRecord[]}
                hasBulkActions={hasBulkActions}
                onSelect={handleOnSelect}
                selectedIds={listContext.selectedIds}
                onToggleItem={(record) => {
                    const recordIdValue = getRecordIdValue(record, recordIdField);
                    if (listContext.selectedIds.includes(recordIdValue)) {
                        listContext.setSelectedIds(
                            listContext.selectedIds.filter((recordA) => recordA !== recordIdValue)
                        );
                    } else {
                        listContext.setSelectedIds([...listContext.selectedIds, recordIdValue]);
                    }
                }}
                onRowClick={onRowClick}
                isSelected={(record) => listContext.selectedIds.includes(getRecordIdValue(record, recordIdField))}
                setSort={(field) => {
                    let order = "asc" as "asc" | "desc";
                    if (field === currentSort.field) {
                        order = currentSort.order === "asc" ? "desc" : "asc";
                    }
                    setSort({
                        field,
                        order,
                    });
                    listContext.changeFetcherParams(
                        Object.assign({}, data.active_filters, {
                            order: field,
                            direction: order,
                            per_page: data.pager.per_page,
                        })
                    );
                }}
                currentSort={currentSort}
            >
                {children}
            </Datagrid>
        </div>
    );
};

export default DatagridWithIds;
